// Integrations API — raw fetch against /api/v1/integrations (P1 config spine;
// api/internal/api/rest/handlers_integrations.go). Not in the generated openapi
// schema, so this mirrors scans.ts' raw-fetch + localStorage-token pattern.
//
//   GET  /integrations                          → { items: Integration[] }
//   PUT  /integrations/{provider}               {display_name, config} → Integration
//   PUT  /integrations/{provider}/streams/{key} {enabled, confirm?, ...} → Integration
//   POST /integrations/{provider}/enable|disable                        → Integration

const BASE = import.meta.env.BASE_URL + "api/v1";

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const tok = localStorage.getItem("bluerock.token");
  return { ...(tok ? { Authorization: `Bearer ${tok}` } : {}), ...(extra ?? {}) };
}

async function explain(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; title?: string; request_id?: string };
    const message =
      typeof body.detail === "string" ? body.detail : typeof body.title === "string" ? body.title : `${fallback} (${res.status})`;
    return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
  } catch {
    return `${fallback} (${res.status})`;
  }
}

export interface SinkConfig {
  tenantId: string;
  subscriptionId: string;
  resourceGroup: string;
  region: string;
  workspaceResourceId: string;
  ingestionEndpoint: string;
  dcrImmutableId: string;
  streamName: string;
  tableName: string;
  identityClientId: string;
  workspaceId: string; // Log Analytics workspace GUID (customerId) — for the read-back
}

// EngineStatus — the live-effective forwarder state (loaded destination + counters +
// breaker), returned alongside the config on GET so the UI shows what the forwarder is
// ACTUALLY doing, not the DB row alone. Absent when no engine is attached (dev).
export interface EngineStatus {
  loaded: boolean;
  master_enabled: boolean;
  config_status: string;
  loaded_endpoint: string;
  loaded_stream_name: string;
  loaded_gen: number;
  config_matches_loaded: boolean; // false ⇒ "pending deploy" / not the saved destination
  reconcile_error?: string;
  counters: { Enqueued: number; Sent: number; Dropped: number; Failed: number };
  breaker_open: boolean;
  audit_cursor: string;
  source_mode: string; // tail | advisor | none
  enforcement_live: boolean;
  last_enforcement_at?: string;
}

// TestResult — the two-phase Test connection outcome.
export interface TestResult {
  outcome: "verified" | "pending" | "accepted" | "error";
  detail: string;
  probe_id: string;
  kql?: string;
}

export interface AzureWorkspace { name: string; resource_id: string; customer_id: string; location: string }
export interface AzureDCR { name: string; resource_id: string; immutable_id: string; ingestion_endpoint?: string; location: string }
export interface AzureDCE { name: string; resource_id: string; ingestion_endpoint?: string; location: string }

export interface Stream {
  stream_key: string;
  enabled: boolean;
  field_allowlist: string[];
  severity_floor: string;
  updated_by: string;
  updated_at: string;
}

export interface Integration {
  id: string;
  provider: string;
  display_name: string;
  master_enabled: boolean;
  sink_config: Partial<SinkConfig>;
  status: "unconfigured" | "configured" | "verified" | "error" | "degraded";
  last_verified_at?: string;
  last_error_code?: string;
  consecutive_failures: number;
  streams: Stream[];
}

const PROVIDER = "sentinel";

export const integrationsApi = {
  get: async (): Promise<{ integration: Integration; live?: EngineStatus }> => {
    const res = await fetch(`${BASE}/integrations`, { cache: "no-store", headers: authHeaders() });
    if (!res.ok) throw new Error(await explain(res, "Could not load integrations"));
    const body = (await res.json()) as { items?: Integration[]; live?: EngineStatus };
    return { integration: (body.items ?? [])[0], live: body.live };
  },
  putConfig: async (config: SinkConfig, displayName: string): Promise<Integration> => {
    const res = await fetch(`${BASE}/integrations/${PROVIDER}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ display_name: displayName, config }),
    });
    if (!res.ok) throw new Error(await explain(res, "Could not save destination"));
    return (await res.json()) as Integration;
  },
  putStream: async (
    key: string,
    body: { enabled: boolean; confirm?: boolean; field_allowlist?: string[]; severity_floor?: string },
  ): Promise<Integration> => {
    const res = await fetch(`${BASE}/integrations/${PROVIDER}/streams/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await explain(res, "Could not update data type"));
    return (await res.json()) as Integration;
  },
  setMaster: async (enabled: boolean): Promise<Integration> => {
    const res = await fetch(`${BASE}/integrations/${PROVIDER}/${enabled ? "enable" : "disable"}`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: "{}",
    });
    if (!res.ok) throw new Error(await explain(res, "Could not change connector state"));
    return (await res.json()) as Integration;
  },
  // Two-phase Test connection: sends a probe row, then reads it back. Returns the
  // outcome (verified|pending|accepted|error). Only `verified` permits enable.
  test: async (): Promise<TestResult> => {
    const res = await fetch(`${BASE}/integrations/${PROVIDER}/test`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: "{}",
    });
    if (!res.ok) throw new Error(await explain(res, "Test connection failed"));
    return (await res.json()) as TestResult;
  },
  // ARM picker — enumerate the customer's resources so the admin selects instead of
  // pasting. Needs the identity clientId + subscription + resource group (RG-scoped).
  azure: {
    workspaces: (q: AzureQuery) => armList<AzureWorkspace>("workspaces", q),
    dcrs: (q: AzureQuery) => armList<AzureDCR>("dcrs", q),
    dces: (q: AzureQuery) => armList<AzureDCE>("dces", q),
  },
};

export interface AzureQuery { identityClientId: string; subscriptionId: string; resourceGroup: string }

async function armList<T>(kind: "workspaces" | "dcrs" | "dces", q: AzureQuery): Promise<T[]> {
  const params = new URLSearchParams({
    identityClientId: q.identityClientId,
    subscriptionId: q.subscriptionId,
    resourceGroup: q.resourceGroup,
  });
  const res = await fetch(`${BASE}/integrations/${PROVIDER}/azure/${kind}?${params.toString()}`, {
    cache: "no-store",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await explain(res, `Could not list ${kind}`));
  const body = (await res.json()) as { items?: T[] };
  return body.items ?? [];
}

// ── view helpers ──────────────────────────────────────────────────────────────

export interface StreamMeta {
  label: string;
  desc: string;
  sensitive: boolean;
}

export const STREAM_META: Record<string, StreamMeta> = {
  audit: { label: "Audit events", desc: "Console actions: policy changes, approvals, access grants.", sensitive: false },
  enforcement: { label: "Enforcement verdicts", desc: "Violations, egress denies, and blocked tool and MCP calls.", sensitive: false },
  process: { label: "Process lifecycle", desc: "Process exec, fork, and terminate events. High volume.", sensitive: false },
  network: { label: "Network activity", desc: "Connection and DNS records. High volume.", sensitive: false },
  argv: { label: "Command-line arguments", desc: "Full command line, not just the binary. May contain secrets passed as arguments.", sensitive: false },
  identity: { label: "Developer identity", desc: "The developer's real email and principal. When off, a stable pseudonymous ID is forwarded instead.", sensitive: true },
  prompt_body: { label: "Prompt and reply bodies", desc: "Raw model input and output. May contain source code, secrets, and personal data.", sensitive: true },
};

export function statusTone(s: Integration["status"]): "success" | "warning" | "danger" | "default" {
  switch (s) {
    case "verified":
      return "success";
    case "error":
      return "danger";
    case "degraded":
      return "warning";
    default:
      return "default";
  }
}
